// D6 — RealityAI Guide: deterministic assistant state machine (TechSpec §26 /
// Master §26). Renderer-independent: it only decides WHICH visual state the
// assistant is in from application events. The renderer (SVG/CSS/Rive/static)
// is a separate concern (renderer abstraction, TechSpec §27).
//
// Invariants:
//  * no LLM-generated emotion — states come ONLY from app events;
//  * deterministic: same event history => same state (testable);
//  * `THINKING` and `SEARCHING` are distinct logical states that MAY be
//    rendered identically (the controller stays semantically clear);
//  * `SLEEP` is a derived idle state (long inactivity), entered and exited
//    purely by time + interaction — no external authority.

import type { AssistantState, GuideResultType } from "./types";

/** An application event that drives the assistant. */
export type GuideEvent =
  | { type: "PANEL_OPEN" }
  | { type: "PANEL_CLOSE" }
  | { type: "QUESTION_SUBMITTED" }
  | { type: "RESULT"; result_type: GuideResultType }
  | { type: "NO_RESULT" }
  | { type: "ERROR" }
  | { type: "FEEDBACK_SENT"; helpful: boolean }
  | { type: "USER_INTERACT" }
  | { type: "IDLE_TIMEOUT" }
  | { type: "RESET" }
  /**
   * A transient state (GREETING / FOUND / ANSWER / SUCCESS / CONFUSED)
   * settled back to its resting state after a short duration. The resting
   * target is deterministic: OPEN-ish states settle to OPEN, everything
   * else to IDLE. This implements the canonical mapping
   * "GREETING → IDLE", "SUCCESS → IDLE", "NO_RESULTS/CONFUSED → IDLE"
   * (Master §26 / D6 directive §16) without coupling the machine to a
   * specific renderer.
   */
  | { type: "SETTLE" };

/** Long-inactivity threshold for SLEEP (ms). Exported for tests. */
export const SLEEP_AFTER_MS = 90_000;

export interface StateMachine {
  readonly state: AssistantState;
  send(event: GuideEvent): AssistantState;
}

/**
 * Pure transition function — the full state table is unit-testable without a
 * component. Time is NOT an input: the SLEEP threshold is the CALLER's job
 * (schedule IDLE_TIMEOUT after `sleepAfterMs` of inactivity), which keeps the
 * transition deterministic and fully table-testable.
 */
export function transition(
  current: AssistantState,
  event: GuideEvent,
): AssistantState {
  switch (event.type) {
    case "PANEL_OPEN":
      // Opening the panel greets, then settles to IDLE/OPEN.
      return "GREETING";
    case "PANEL_CLOSE":
      // Closing returns to the idle closed state.
      return "IDLE";
    case "QUESTION_SUBMITTED":
      return "THINKING";
    case "RESULT":
      // SEARCH is the only result type that visually stays "searching-ish";
      // FAQ/troubleshooting/search all resolve to FOUND (then ANSWER for a
      // rendered answer). We map to FOUND so the renderer can show the
      // result; a separate ANSWER is entered when the answer area paints.
      return "FOUND";
    case "NO_RESULT":
      return "NO_RESULTS";
    case "ERROR":
      return "ERROR";
    case "FEEDBACK_SENT":
      // Helpful => short SUCCESS; Not Helpful => subtle CONFUSED.
      return event.helpful ? "SUCCESS" : "CONFUSED";
    case "SETTLE":
      // Transient states settle to their resting state (deterministic):
      //   GREETING/FOUND/ANSWER/SUCCESS/CONFUSED -> OPEN if the panel is
      //   open (the caller only sends SETTLE while open), else IDLE.
      //   NO_RESULTS/ERROR stay until the next interaction (the robot
      //   keeps showing the outcome; USER_INTERACT clears them).
      //   OPEN/IDLE/HOVER/WAITING/SLEEP/THINKING/SEARCHING/CLOSED: no-op.
      switch (current) {
        case "GREETING":
        case "FOUND":
        case "ANSWER":
        case "SUCCESS":
        case "CONFUSED":
          return "OPEN";
        default:
          return current;
      }
    case "USER_INTERACT":
      // Any interaction wakes the assistant (exit SLEEP/ERROR/NO_RESULTS).
      if (current === "SLEEP" || current === "ERROR" || current === "NO_RESULTS" || current === "CONFUSED") {
        return "IDLE";
      }
      return current === "CLOSED" ? "HOVER" : "IDLE";
    case "IDLE_TIMEOUT":
      // Derived SLEEP: only from a settled idle-ish state, and only after the
      // threshold has elapsed (the caller schedules this at sleepAfterMs).
      if (current === "IDLE" || current === "OPEN" || current === "GREETING") {
        return "SLEEP";
      }
      return current;
    case "RESET":
      return "IDLE";
  }
}

/**
 * A small state machine instance. Tracks the current state and exposes
 * `send`. Time-based SLEEP is the caller's responsibility (schedule an
 * IDLE_TIMEOUT after `sleepAfterMs` of inactivity, clear it on interaction)
 * so the machine stays deterministic and leak-free.
 */
export function createStateMachine(
  initial: AssistantState = "IDLE",
): StateMachine {
  let state = initial;
  return {
    get state() {
      return state;
    },
    send(event: GuideEvent): AssistantState {
      state = transition(state, event);
      return state;
    },
  };
}

/**
 * Which states allow the panel to be interacted with (input enabled,
 * results rendered). Exported for the panel component.
 */
export function isPanelActiveState(s: AssistantState): boolean {
  return (
    s === "OPEN" ||
    s === "GREETING" ||
    s === "THINKING" ||
    s === "SEARCHING" ||
    s === "FOUND" ||
    s === "ANSWER" ||
    s === "NO_RESULTS" ||
    s === "ERROR" ||
    s === "SUCCESS" ||
    s === "CONFUSED"
  );
}

/**
 * Which states show a "loading/thinking" indicator in the panel.
 */
export function isBusyState(s: AssistantState): boolean {
  return s === "THINKING" || s === "SEARCHING" || s === "WAITING";
}
